"use client";

import * as React from "react";
import GridLayout, { Layout, LayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import type {
  Chart,
  Report,
  GridPosition,
  ReportQueryResult,
} from "@bi-studio/shared/lib";
import {
  Table,
  ActionIcon,
  Loader,
  useMantineColorScheme,
  Box,
  Text,
  rem,
} from "@mantine/core";
import { Trash2, GripVertical, AlertCircle } from "lucide-react";
import { executeReport } from "@bi-studio/shared/apis";
import { renderChart } from "@/charts";

interface ChartWithLayout {
  chart: Chart;
  layout: GridPosition;
  id: string; // Unique ID for grid item
  type: "chart";
}

interface ReportWithLayout {
  report: Report;
  layout: GridPosition;
  id: string; // Unique ID for grid item
  type: "report";
}

type ItemWithLayout = ChartWithLayout | ReportWithLayout;

interface DashboardGridLayoutProps {
  charts: Chart[];
  reports?: Report[];
  chartLayouts: Map<string, GridPosition>;
  reportLayouts?: Map<string, GridPosition>;
  onChartLayoutChange: (chartId: string, layout: GridPosition) => void;
  onReportLayoutChange?: (reportId: string, layout: GridPosition) => void;
  onRemoveChart: (chartId: string) => void;
  onRemoveReport?: (reportId: string) => void;
  reportData?: Map<string, ReportQueryResult>;
  loadingReports?: Set<string>;
  token: string;
  readOnly?: boolean; // If true, disable drag and resize
}

type ChartDefinition =
  | {
    type: "bar";
    config: any & {
      xField: string;
      yField: string;
      orientation?: "vertical" | "horizontal";
    };
  }
  | {
    type: "line";
    config: any & {
      xField: string;
      yField: string;
      curve?: "linear" | "basis" | "step";
      seriesField?: string;
    };
  }
  | {
    type: "pie";
    config: any & {
      labelField: string;
      valueField: string;
      innerRadius?: number;
    };
  }
  | {
    type: "scorecard";
    config: any & {
      valueField: string;
      rowIndex?: number;
    };
  };

export function DashboardGridLayout({
  charts,
  reports = [],
  chartLayouts,
  reportLayouts = new Map(),
  onChartLayoutChange,
  onReportLayoutChange,
  onRemoveChart,
  onRemoveReport,
  reportData = new Map(),
  loadingReports = new Set(),
  token,
  readOnly = false,
}: DashboardGridLayoutProps) {
  const { colorScheme } = useMantineColorScheme();
  const chartContainerRefs = React.useRef<Map<string, HTMLDivElement>>(
    new Map()
  );
  const gridContainerRef = React.useRef<HTMLDivElement>(null);
  const [chartData, setChartData] = React.useState<
    Map<string, ReportQueryResult>
  >(new Map());
  const [loadingCharts, setLoadingCharts] = React.useState<Set<string>>(
    new Set()
  );
  const [gridWidth, setGridWidth] = React.useState(1200);
  const [isMobile, setIsMobile] = React.useState(false);

  // Combine all layouts to check for saved container width
  const allLayouts = React.useMemo(() => {
    return [
      ...Array.from(chartLayouts.values()),
      ...Array.from(reportLayouts.values()),
    ];
  }, [chartLayouts, reportLayouts]);

  // Restore saved container width when layouts are loaded (before calculating from viewport)
  React.useEffect(() => {
    if (allLayouts.length > 0) {
      // Find the first layout with a saved container width
      for (const layout of allLayouts) {
        if (layout.containerWidth && layout.containerWidth > 0) {
          setGridWidth(layout.containerWidth);
          return; // Use the first saved width found
        }
      }
    }
  }, [allLayouts]);

  // Calculate grid width based on container (responsive, but respects saved width if available)
  React.useEffect(() => {
    const updateWidth = () => {
      if (gridContainerRef.current) {
        const containerWidth = gridContainerRef.current.clientWidth;

        const isNowMobile = containerWidth < 768; // Standard tablet/mobile breakpoint
        setIsMobile(isNowMobile);

        const calculatedWidth = Math.max(containerWidth - 32, 300); // Subtract padding, min 300px

        // Check if we have a saved container width
        let savedWidth: number | undefined;
        for (const layout of allLayouts) {
          if (layout.containerWidth && layout.containerWidth > 0) {
            savedWidth = layout.containerWidth;
            break;
          }
        }

        // Use saved width if it's reasonable (not too much larger than viewport)
        // Otherwise use calculated width for responsiveness
        if (savedWidth && savedWidth <= calculatedWidth * 1.5 && !isNowMobile) {
          setGridWidth(Math.min(savedWidth, calculatedWidth));
        } else {
          setGridWidth(calculatedWidth);
        }
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [allLayouts]);

  // Convert charts and reports to grid layout format
  const gridItems: ItemWithLayout[] = React.useMemo(() => {
    const items: ItemWithLayout[] = [];

    // Add charts
    charts.forEach((chart) => {
      const layout = chartLayouts.get(chart.id) || { x: 0, y: 0, w: 6, h: 4 };
      items.push({
        chart,
        layout,
        id: chart.id,
        type: "chart",
      });
    });

    // Add reports
    reports.forEach((report) => {
      const layout = reportLayouts.get(report.id) || { x: 0, y: 0, w: 6, h: 4 };
      items.push({
        report,
        layout,
        id: report.id,
        type: "report",
      });
    });

    return items;
  }, [charts, reports, chartLayouts, reportLayouts]);

  // Convert to react-grid-layout format
  const gridLayout: Layout = React.useMemo(() => {
    return gridItems.map(
      (item): LayoutItem => ({
        i: item.id,
        x: item.layout.x,
        y: item.layout.y,
        w: item.layout.w,
        h: item.layout.h,
        minW: 2,
        minH: 2,
      })
    );
  }, [gridItems]);

  // Load chart data for preview
  React.useEffect(() => {
    const loadChartData = async (chart: Chart) => {
      if (chartData.has(chart.id) || loadingCharts.has(chart.id)) {
        return;
      }

      try {
        setLoadingCharts((prev) => new Set(prev).add(chart.id));
        const result = await executeReport(token, chart.reportId, {
          pagination: { page: 1, pageSize: 0 },
        });
        setChartData((prev) => new Map(prev).set(chart.id, result));
      } catch (err) {
        console.error(`Failed to load data for chart ${chart.id}:`, err);
      } finally {
        setLoadingCharts((prev) => {
          const next = new Set(prev);
          next.delete(chart.id);
          return next;
        });
      }
    };

    charts.forEach((chart) => {
      loadChartData(chart);
    });
  }, [charts, token, chartData, loadingCharts]);

  // Handle layout change from react-grid-layout
  const handleLayoutChange = (layout: Layout) => {
    layout.forEach((item) => {
      const itemId = item.i;
      const gridItem = gridItems.find((gi) => gi.id === itemId);
      if (!gridItem) return;

      const newLayout: GridPosition = {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        containerWidth: gridWidth, // Save container width for consistent rendering
      };

      if (gridItem.type === "chart") {
        const existingLayout = chartLayouts.get(itemId);
        if (
          existingLayout &&
          (existingLayout.x !== item.x ||
            existingLayout.y !== item.y ||
            existingLayout.w !== item.w ||
            existingLayout.h !== item.h)
        ) {
          onChartLayoutChange(itemId, newLayout);
        }
      } else if (gridItem.type === "report" && onReportLayoutChange) {
        const existingLayout = reportLayouts.get(itemId);
        if (
          existingLayout &&
          (existingLayout.x !== item.x ||
            existingLayout.y !== item.y ||
            existingLayout.w !== item.w ||
            existingLayout.h !== item.h)
        ) {
          onReportLayoutChange(itemId, newLayout);
        }
      }
    });
  };

  // Render charts when data is available or layout changes
  React.useEffect(() => {
    // Small delay to ensure DOM has updated after resize
    const timeoutId = setTimeout(() => {
      gridItems.forEach((item) => {
        if (item.type !== "chart") return;

        const container = chartContainerRefs.current.get(item.id);
        const data = chartData.get(item.chart.id);

        if (!container || !data || !data.rows || data.rows.length === 0) {
          return;
        }

        try {
          const definition: ChartDefinition = {
            type: item.chart.type,
            config: item.chart.config,
          } as ChartDefinition;

          // Get container dimensions for responsive sizing
          const containerWidth = container.clientWidth || 400;
          // Use fixed height on mobile/fallback, or dynamic
          const containerHeight = isMobile ? 300 : (container.clientHeight || 300);

          const chartWidth = Math.max(containerWidth - 32, 200);
          const chartHeight = Math.max(containerHeight - 80, 200);

          void renderChart(container, data.rows, definition, {
            width: chartWidth,
            height: chartHeight,
            margin: { top: 32, right: 24, bottom: 48, left: 56 },
          }, (colorScheme === 'dark' ? 'dark' : 'light'));
        } catch (err) {
          console.error(`Failed to render chart ${item.id}:`, err);
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [gridItems, chartData, gridLayout, isMobile, colorScheme]);

  const setChartRef = (chartId: string) => (el: HTMLDivElement | null) => {
    if (el) {
      chartContainerRefs.current.set(chartId, el);
    } else {
      chartContainerRefs.current.delete(chartId);
    }
  };

  const renderItemContent = (item: ItemWithLayout) => {
    if (item.type === "chart") {
      const data = chartData.get(item.chart.id);
      const isLoading = loadingCharts.has(item.chart.id);
      const hasData = data && data.rows && data.rows.length > 0;

      return (
        <Box
          key={item.id}
          style={{
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.7)',
            borderRadius: 'var(--mantine-radius-md)',
            border: '1px solid var(--modal-border)',
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--mantine-shadow-sm)',
          }}
        >
          <Box
            className={readOnly ? "" : "drag-handle cursor-move"}
            style={{
              padding: `${rem(8)} ${rem(12)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--modal-border)',
              backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'center', gap: rem(8), flex: 1, minWidth: 0 }}>
              {!readOnly && <GripVertical size={14} className="text-muted-foreground shrink-0" />}
              <Text size="sm" fw={600} className="truncate">
                {item.chart.config.title || `Chart ${item.chart.id.substring(0, 8)}`}
              </Text>
            </Box>
            {!readOnly && (
              <ActionIcon
                color="red"
                variant="subtle"
                size="sm"
                onClick={() => onRemoveChart(item.chart.id)}
                title="Remove chart"
              >
                <Trash2 size={16} />
              </ActionIcon>
            )}
          </Box>
          <Box style={{ padding: rem(16), flex: 1, overflow: 'hidden', position: 'relative', height: isMobile ? rem(300) : 'auto' }}>
            {isLoading ? (
              <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyItems: 'center', justifyContent: 'center' }}>
                <Loader size="sm" />
              </Box>
            ) : hasData ? (
              <div
                ref={setChartRef(item.id)}
                className="w-full h-full"
                style={{ minHeight: "200px" }}
              />
            ) : (
              <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Text size="sm" c="dimmed">No data available</Text>
              </Box>
            )}
          </Box>
        </Box>
      );
    } else {
      // Report item
      const data = reportData.get(item.report.id);
      const isLoading = loadingReports.has(item.report.id);
      const hasData = data && data.rows && data.rows.length > 0;

      return (
        <Box
          key={item.id}
          style={{
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.7)',
            borderRadius: 'var(--mantine-radius-md)',
            border: '1px solid var(--modal-border)',
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--mantine-shadow-sm)',
          }}
        >
          <Box
            className={readOnly ? "" : "drag-handle cursor-move"}
            style={{
              padding: `${rem(8)} ${rem(12)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--modal-border)',
              backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'center', gap: rem(8), flex: 1, minWidth: 0 }}>
              {!readOnly && <GripVertical size={14} className="text-muted-foreground shrink-0" />}
              <Text size="sm" fw={600} className="truncate">{item.report.name}</Text>
            </Box>
            {!readOnly && onRemoveReport && (
              <ActionIcon
                color="red"
                variant="subtle"
                size="sm"
                onClick={() => onRemoveReport(item.report.id)}
                title="Remove report"
              >
                <Trash2 size={16} />
              </ActionIcon>
            )}
          </Box>
          <Box style={{ padding: rem(16), flex: 1, overflow: 'auto' }}>
            {isLoading ? (
              <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Loader size="sm" />
              </Box>
            ) : hasData ? (
              <div className="overflow-x-auto h-full container-queries">
                <Table.ScrollContainer minWidth={500} className={isMobile ? "max-h-[400px]" : "h-full"}>
                  <Table highlightOnHover stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        {data!.rows[0] &&
                          Object.keys(data!.rows[0]).map((key) => (
                            <Table.Th key={key} style={{ whiteSpace: 'nowrap' }}>{key}</Table.Th>
                          ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data!.rows.map((row, idx) => (
                        <Table.Tr key={idx}>
                          {Object.values(row).map((value, cellIdx) => (
                            <Table.Td key={cellIdx} style={{ whiteSpace: 'nowrap' }}>
                              {value === null || value === undefined
                                ? "NULL"
                                : String(value)}
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </div>
            ) : (
              <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Text size="sm" c="dimmed">No data available</Text>
              </Box>
            )}
          </Box>
        </Box>
      );
    }
  };

  if (gridItems.length === 0) {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: rem(256),
          alignItems: 'center',
          justifyContent: 'center',
          padding: rem(32),
          textAlign: 'center',
          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
          borderRadius: 'var(--mantine-radius-md)',
          border: '1px dashed var(--modal-border)',
          backdropFilter: 'blur(5px)',
        }}
      >
        <AlertCircle size={32} className="text-muted-foreground" style={{ marginBottom: rem(12), opacity: 0.5 }} />
        <Text size="sm" c="dimmed" style={{ maxWidth: rem(320) }}>
          No items added. Select charts and reports from the dropdown above to add
          them to the grid.
        </Text>
      </Box>
    );
  }

  return (
    <div ref={gridContainerRef} className="w-full">
      {isMobile ? (
        <div className="flex flex-col gap-4">
          {gridItems.map((item) => (
            <div key={item.id} className="w-full">
              {renderItemContent(item)}
            </div>
          ))}
        </div>
      ) : (
        <GridLayout
          className="layout"
          layout={gridLayout}
          width={gridWidth}
          onLayoutChange={readOnly ? undefined : handleLayoutChange}
          gridConfig={{
            cols: 24,
            rowHeight: 30,
          }}
          dragConfig={{
            enabled: !readOnly,
            handle: ".drag-handle",
          }}
          resizeConfig={{
            enabled: !readOnly,
          }}
        >
          {gridItems.map((item) => (
            <div key={item.id} className="h-full">
              {renderItemContent(item)}
            </div>
          ))}
        </GridLayout>
      )}
    </div>
  );
}
